import { appStore, IEvent, MessageBus } from '@tom-ui/utils'
import { RoadNodeDto, RoadSegmentDto } from '@yard/features/road-network/models/road-network.model'
import { EventTypes } from '@yard/messages/eventsTypes'
import { DeleteResponse } from '@yard/messages/models/delete-response.model'
import { IRoadNetworkService } from '@yard/services/road-network.service'
import { action, makeObservable, observable, runInAction } from 'mobx'

export class RoadNetworkStore {
  nodes: RoadNodeDto[] = []
  segments: RoadSegmentDto[] = []

  private isFetched = false
  private readonly nodeUpdateTimers = new Map<string, ReturnType<typeof setTimeout>>()
  private readonly disposers: Array<() => void> = []

  constructor(
    private readonly service: IRoadNetworkService,
    messageBus: MessageBus,
  ) {
    makeObservable(this, {
      nodes: observable,
      segments: observable,
      loadAll: action,
      createNode: action,
      updateNode: action,
      deleteNode: action,
      createSegment: action,
      updateSegment: action,
      deleteSegment: action,
      addNodeOptimistic: action,
      addSegmentOptimistic: action,
      removeNodeOptimistic: action,
      removeSegmentOptimistic: action,
      updateNodeOptimistic: action,
      updateSegmentOptimistic: action,
      replaceNodeId: action,
      replaceSegmentId: action,
    })

    this.disposers.push(
      messageBus.subscribeEvent<RoadNodeDto>(
        EventTypes.RoadNodeUpsertedEvent,
        this.handleNodeUpserted,
      ),
      messageBus.subscribeEvent<DeleteResponse>(
        EventTypes.RoadNodeDeletedEvent,
        this.handleNodeDeleted,
      ),
      messageBus.subscribeEvent<RoadSegmentDto>(
        EventTypes.RoadSegmentUpsertedEvent,
        this.handleSegmentUpserted,
      ),
      messageBus.subscribeEvent<DeleteResponse>(
        EventTypes.RoadSegmentDeletedEvent,
        this.handleSegmentDeleted,
      ),
    )
  }

  dispose(): void {
    this.disposers.forEach(d => d())
    this.disposers.length = 0
    this.nodeUpdateTimers.forEach(timer => clearTimeout(timer))
    this.nodeUpdateTimers.clear()
  }

  private handleNodeUpserted = (event: IEvent<RoadNodeDto>) => {
    const node = event.payload
    if (!node) return
    runInAction(() => {
      const idx = this.nodes.findIndex(n => n.id === node.id)
      if (idx >= 0) {
        this.nodes = this.nodes.map((n, i) => (i === idx ? node : n))
      } else {
        this.nodes = [...this.nodes, node]
      }
    })
  }

  private handleNodeDeleted = (event: IEvent<DeleteResponse>) => {
    const response = event.payload
    if (!response) return
    runInAction(() => {
      this.nodes = this.nodes.filter(n => n.id !== response.id)
      this.segments = this.segments.filter(
        s => s.startNodeId !== response.id && s.endNodeId !== response.id,
      )
    })
  }

  private handleSegmentUpserted = (event: IEvent<RoadSegmentDto>) => {
    const segment = event.payload
    if (!segment) return
    runInAction(() => {
      const idx = this.segments.findIndex(s => s.id === segment.id)
      if (idx >= 0) {
        this.segments = this.segments.map((s, i) => (i === idx ? segment : s))
      } else {
        this.segments = [...this.segments, segment]
      }
    })
  }

  private handleSegmentDeleted = (event: IEvent<DeleteResponse>) => {
    const response = event.payload
    if (!response) return
    runInAction(() => {
      this.segments = this.segments.filter(s => s.id !== response.id)
    })
  }

  async loadAll() {
    if (this.isFetched) return
    try {
      const [nodes, segments] = await Promise.all([
        this.service.getNodes(),
        this.service.getSegments(),
      ])
      runInAction(() => {
        this.nodes = nodes
        this.segments = segments
        this.isFetched = true
      })
    } catch (e) {
      appStore.setShowAlert('error', (e as any)?.response?.data?.detail ?? 'An error occurred')
    }
  }

  async createNode(nodeData: Omit<RoadNodeDto, 'id'>): Promise<RoadNodeDto> {
    return this.service.createNode(nodeData)
  }

  async updateNode(node: RoadNodeDto): Promise<void> {
    await this.service.updateNode(node)
  }

  async deleteNode(id: string): Promise<void> {
    await this.service.deleteNode(id)
  }

  async createSegment(segmentData: Omit<RoadSegmentDto, 'id'>): Promise<RoadSegmentDto> {
    return this.service.createSegment(segmentData)
  }

  async updateSegment(segment: RoadSegmentDto): Promise<void> {
    await this.service.updateSegment(segment)
  }

  async deleteSegment(id: string): Promise<void> {
    await this.service.deleteSegment(id)
  }

  addNodeOptimistic(node: RoadNodeDto): void {
    this.nodes = [...this.nodes, node]
  }

  addSegmentOptimistic(segment: RoadSegmentDto): void {
    this.segments = [...this.segments, segment]
  }

  removeNodeOptimistic(id: string): void {
    this.nodes = this.nodes.filter(n => n.id !== id)
    this.segments = this.segments.filter(s => s.startNodeId !== id && s.endNodeId !== id)
  }

  removeSegmentOptimistic(id: string): void {
    this.segments = this.segments.filter(s => s.id !== id)
  }

  updateNodeOptimistic(node: RoadNodeDto): void {
    this.nodes = this.nodes.map(n => (n.id === node.id ? node : n))
  }

  updateSegmentOptimistic(segment: RoadSegmentDto): void {
    this.segments = this.segments.map(s => (s.id === segment.id ? segment : s))
  }

  replaceNodeId(tempId: string, realNode: RoadNodeDto): void {
    const alreadyHasReal = this.nodes.some(n => n.id === realNode.id)
    if (alreadyHasReal) {
      this.nodes = this.nodes.filter(n => n.id !== tempId)
    } else {
      this.nodes = this.nodes.map(n => (n.id === tempId ? realNode : n))
    }
    this.segments = this.segments.map(s => ({
      ...s,
      startNodeId: s.startNodeId === tempId ? realNode.id : s.startNodeId,
      endNodeId: s.endNodeId === tempId ? realNode.id : s.endNodeId,
    }))
  }

  replaceSegmentId(tempId: string, realSegment: RoadSegmentDto): void {
    const alreadyHasReal = this.segments.some(s => s.id === realSegment.id)
    if (alreadyHasReal) {
      this.segments = this.segments.filter(s => s.id !== tempId)
    } else {
      this.segments = this.segments.map(s => (s.id === tempId ? realSegment : s))
    }
  }

  /**
   * Debounced persist for cases where the same node may be updated multiple times in quick
   * succession (e.g. rapid rotation). Cancels any pending persist for this node and schedules
   * a new one that reads the latest optimistic position from the store when it fires, so only
   * one API call is made and stale-event race conditions are avoided.
   */
  debouncedUpdateNode(nodeId: string, delayMs = 300): void {
    const existing = this.nodeUpdateTimers.get(nodeId)
    if (existing) clearTimeout(existing)

    this.nodeUpdateTimers.set(
      nodeId,
      setTimeout(() => {
        this.nodeUpdateTimers.delete(nodeId)
        const current = this.nodes.find(n => n.id === nodeId)
        if (current)
          this.updateNode(current).catch(() => {
            return
          })
      }, delayMs),
    )
  }
}
